import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Header.scss";
import {
  MotifHeader,
  MotifHeaderLogo,
  MotifAvatar,
  MotifIconButton,
} from "@ey-xd/motif-react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import {
  getPhoto,
  setAllowedOrderIds,
  setSelectedRole,
} from "../../store/slices/userSlice";
import { fetchNotifications } from "../../store/slices/notificationsSlice";
import {
  appName,
  APP_SUBTITLE,
  HEADER,
  ARIA_LABELS,
} from "../../util/constants";
import { routes } from "../../routes/routes.constatns";
import { useGetTokenForImage } from "../../hooks/useAppToken";
import NotificationsPanel from "../Notifications/NotificationsPanel";
import HeaderLogo from "../../assets/Images/HeaderLogo.png";
import { CheckIcon } from "../../assets/Icons/CheckIcon";
import FilterDropdownDownArrow from "../../assets/Icons/FilterDropdownDownArrow";
import { getNotificationRoleSegment } from "../../util/notificationRole";
import { OrdersService } from "../../api/services";
import { Notificationwithdot } from "../../assets/Icons/Notificationwithdot";
import { HempCircleIcon } from "../../assets/Icons/HempCircleIcon";
import  BookIcon  from "../../assets/Icons/BookIcon";
import { HeadsetHelpIcon } from "../../assets/Icons/HeadsetHelpIcon";
import { LogoutIcon } from "../../assets/Icons/LogoutIcon";
import { ChatHelpIcon } from "../../assets/Icons/ChatHelpIcon";
import { PageIcon } from "../../assets/Icons/PageIcon";
import { PrivacyPolicyIcon } from "../../assets/Icons/PrivacyPolicyIcon";
import { MailIcon } from "../../assets/Icons/MailIcon";
import PrivacyPolicyPDF from "../../assets/templates/Privacy_Notice_GDS ai Axon.pdf";

const getRoleName = (role) =>
  typeof role === "object" ? role?.roleName || role?.name || "" : role || "";

const isRoleActive = (role) => {
  if (typeof role !== "object" || role === null) {
    return true;
  }

  if (typeof role.isActive === "boolean") {
    return role.isActive;
  }

  if (typeof role.status === "boolean") {
    return role.status;
  }

  if (typeof role.status === "string") {
    return role.status.trim().toLowerCase() !== "inactive";
  }

  return true;
};

// Main application header with logo, notifications, role selector, and user avatar
function Header({ setSideMenuCollapsed = () => {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const user = useSelector((state) => state.user.user);
  const avatar = useSelector((state) => state.user.photo);
  const userRolesFromRedux = useSelector((state) => state.user.userRoles);
  const rolesLoaded = useSelector((state) => state.user.rolesLoaded);
  const selectedRoleFromRedux = useSelector((state) => state.user.selectedRole);
  const currentOrder = useSelector((state) => state.orders?.currentOrder);
  const notificationsLoading = useSelector(
    (state) => state.notifications.loading,
  );
  const token = useGetTokenForImage();
  const [isNotifOpen, setNotifOpen] = useState(false);
  const [isRoleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [isHelpDropdownOpen, setHelpDropdownOpen] = useState(false);

  // Help menu links - update these URLs as needed
  const helpMenuLinks = {
    functionalGuide: "https://sites.ey.com/:b:/r/sites/gdsaiaxon/Shared%20Documents/GDS.ai%20Axon_Functional%20Guide_v1..pdf?csf=1&web=1&e=SFv05u", // Add your link here
    privacyPolicy: "", // Add your link here
    contactSupport: HEADER.CONTACT_SUPPORT_URL || "", 
  };

  // Use roles from Redux if available, otherwise fallback to constants
  const userRoles =
    userRolesFromRedux?.length > 0 ? userRolesFromRedux : HEADER.USER_ROLES;

  // Get distinct roles by roleName, keeping the first occurrence (for userGuid)
  // Filter out Admin role (case-insensitive) and inactive roles
  const distinctRoles = useMemo(() => {
    if (!Array.isArray(userRoles)) return [];
    const seenRoleNames = new Set();
    return userRoles.filter((role) => {
      const roleName = getRoleName(role);
      const isActive = isRoleActive(role);

      // Skip Admin role (case-insensitive) and inactive roles
      if (roleName?.toLowerCase() === "admin" || !isActive) {
        return false;
      }

      if (seenRoleNames.has(roleName)) {
        return false;
      }
      seenRoleNames.add(roleName);
      return true;
    });
  }, [userRoles]);

  // Find DID role to set as default, otherwise use first role
  const defaultRole = useMemo(() => {
    if (!Array.isArray(distinctRoles) || distinctRoles.length === 0)
      return null;

    // Check if DID role exists
    const didRole = distinctRoles.find((role) => {
      const roleName = getRoleName(role);
      return roleName?.toUpperCase() === "DID";
    });

    // Return DID role if found, otherwise return first role
    return didRole || distinctRoles[0];
  }, [distinctRoles]);

  const selectedRole = selectedRoleFromRedux || defaultRole;

  const isHomeScreen = location.pathname === "/" || location.pathname === "/home";
  const hasNoRoles =
    rolesLoaded &&
    (!Array.isArray(userRolesFromRedux) || userRolesFromRedux.length === 0);
  const shouldDisableHeaderActions = isHomeScreen && hasNoRoles;

  // Check if all non-admin roles are inactive
  const allRolesInactive = useMemo(() => {
    if (!Array.isArray(userRoles) || userRoles.length === 0) return false;
    return userRoles.every((role) => {
      const roleName = getRoleName(role);
      const isActive = isRoleActive(role);
      // Skip admin role when checking - only check non-admin roles
      if (roleName?.toLowerCase() === "admin") return true;
      return !isActive;
    });
  }, [userRoles]);

  useEffect(() => {
    if (!shouldDisableHeaderActions) {
      return;
    }

    setNotifOpen(false);
    setRoleDropdownOpen(false);
    setHelpDropdownOpen(false);
  }, [shouldDisableHeaderActions]);

  // Handles notification icon click - fetches notifications and opens panel
  // Scenario 5: Fetch AI orders when notifications icon is clicked
  const handleNotificationIconClick = useCallback(async () => {
    const userEmail =
      user?.email ||
      user?.mail ||
      user?.username ||
      user?.preferred_username ||
      user?.upn ||
      "";
    const notificationRole = getNotificationRoleSegment(selectedRole);
    const roleId = selectedRole?.roleId || "";
    setNotifOpen((o) => !o);
    if (userEmail && notificationRole) {
      dispatch(
        fetchNotifications({ email: userEmail, role: selectedRole, roleId }),
      );
    }
    // Fetch AI-allowed order IDs when clicking notifications icon
    if (roleId) {
      try {
        const response = await OrdersService.getAIOrders(roleId);

        // Extract orderIds from response and convert to strings
        const rawOrderIds = Array.isArray(response)
          ? response
          : response?.data?.orderIds || response?.orderIds || [];
        const orderIds = rawOrderIds.map((id) => String(id));

        dispatch(setAllowedOrderIds(orderIds));
      } catch (error) {
        console.error("Error fetching AI allowed orders:", error);
      }
    }
  }, [dispatch, selectedRole, user]);

  // Updates selected role, closes dropdown, and navigates to home
  const handleRoleSelect = (role) => {
    dispatch(setSelectedRole(role));
    setRoleDropdownOpen(false);
    // Navigate to home screen when role is switched
    navigate(routes.home);
  };

  // Handles user logout - clears session and redirects to login
  const handleLogout = useCallback(() => {
    setRoleDropdownOpen(false);
    
    // Clear JWT token from localStorage
    localStorage.removeItem("axon_auth_token");
    localStorage.removeItem("axon_auth_token_expires_at");
    
    // Clear AI thread ID from sessionStorage
    sessionStorage.removeItem("axon_ai_thread_id");
    
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance]);

  // Navigates to home page when logo is clicked
  async function onLogoClickHandler() {
    navigate(routes.home);
  }

  // Get the photo from user name
  useEffect(() => {
    if (user && user?.username) {
      if (!avatar && token) dispatch(getPhoto({ token: token }));
    }
  }, [dispatch, user, user?.username, avatar, token]);
  return (
    <>
      <MotifHeader
        className="app-header"
        appHeaderName={
          <div className="app-header-title">
            <div className="app-header-name">{appName}</div>
            <div className="app-header-subtitle">{APP_SUBTITLE}</div>
          </div>
        }
        logo={
          <MotifHeaderLogo>
            <a
              href="/"
              aria-label="EY Logo - Home"
              onClick={() => {
                return onLogoClickHandler();
              }}
              className="app-header-logo"
            >
              <img
                aria-hidden="true"
                alt={HEADER.LOGO_ALT}
                src={HeaderLogo}
                height={"40px"}
                width={"40px"}
              />
            </a>
          </MotifHeaderLogo>
        }
        iconsContainer={
          <div className="app-header-right-components">
            <div className="support-icon-wrapper"></div>
            <div className="help-icon-wrapper">
              <MotifIconButton
                aria-label="Help Menu"
                onClick={() =>
                  !shouldDisableHeaderActions &&
                  setHelpDropdownOpen(!isHelpDropdownOpen)
                }
                title="Help"
                className="help-icon-button"
                disabled={shouldDisableHeaderActions}
              >
                <HempCircleIcon width={20} height={20} color="#a4a3b1" />
              </MotifIconButton>
              {isHelpDropdownOpen && (
                <>
                  <div
                    className="help-dropdown-overlay"
                    onClick={() => setHelpDropdownOpen(false)}
                  />
                  <div className="help-dropdown-menu">
                    <div
                      className="help-dropdown-item"
                      onClick={() => {
                        if (helpMenuLinks.functionalGuide) {
                          window.open(helpMenuLinks.functionalGuide, "_blank");
                        }
                        setHelpDropdownOpen(false);
                      }}
                    >
                      <PageIcon width={18} height={18} color="#a4a3b1" />
                      <span>Functional Guide</span>
                    </div>
                    <div
                      className="help-dropdown-item"
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = PrivacyPolicyPDF;
                        link.download = "Privacy_Notice_GDS_ai_Axon.pdf";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        setHelpDropdownOpen(false);
                      }}
                    >
                      <PrivacyPolicyIcon width={18} height={18} color="#a4a3b1" />
                      <span>Privacy Policy</span>
                    </div>
                    <div
                      className="help-dropdown-item"
                      onClick={() => {
                        if (helpMenuLinks.contactSupport) {
                          window.open(helpMenuLinks.contactSupport, "_blank");
                        }
                        setHelpDropdownOpen(false);
                      }}
                    >
                      <HeadsetHelpIcon width={18} height={18} color="#a4a3b1" />
                      <span>Contact Support</span>
                    </div>
                    <div
                      className="help-dropdown-item"
                      onClick={() => {
                        const orderNumber = currentOrder?.[0]?.headerSection?.[0]?.requestId || "Unknown";
                        const recipients = "Jerry.John.Varghese@gds.ey.com;Pinchu.Thomas@gds.ey.com";
                        const subject = encodeURIComponent(`GDS.ai Axon Support${orderNumber !== "Unknown" ?  ` - Order ${orderNumber}` : ""}`);
                        window.location.href = `mailto:${recipients}?subject=${subject}`;
                        setHelpDropdownOpen(false);
                      }}
                    >
                      <MailIcon width={18} height={18} color="#a4a3b1" />
                      <span>Business Support</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="notification-icon-wrapper">
              {/** Disable notifications when roles are inactive or user has no roles on home. */}
              <MotifIconButton
                aria-label={ARIA_LABELS.NOTIFICATIONS}
                onClick={
                  !allRolesInactive && !shouldDisableHeaderActions
                    ? handleNotificationIconClick
                    : undefined
                }
                disabled={allRolesInactive || shouldDisableHeaderActions}
                className={
                  allRolesInactive || shouldDisableHeaderActions
                    ? "disabled-notification"
                    : "enabled-notification"
                }
              >
                <Notificationwithdot width={20} height={20} color="#a4a3b1" />
              </MotifIconButton>
            </div>
            <div
              className={`user-profile-section ${
                shouldDisableHeaderActions ? "disabled-user-profile" : ""
              }`}
              onClick={() =>
                !shouldDisableHeaderActions &&
                setRoleDropdownOpen(!isRoleDropdownOpen)
              }
              aria-disabled={shouldDisableHeaderActions}
            >
              <MotifAvatar
                description={user?.name}
                size="medium"
                userName={user?.name}
                src={avatar}
              />
              <div className="user-info-box">
                <div className="user-info-name">
                  {user?.name || HEADER.USER_NAME_FALLBACK}
                </div>
                <div className="user-info-designation">
                  {selectedRole?.roleName || HEADER.DESIGNATION_FALLBACK}
                </div>
              </div>
              <div className="user-info-arrow">
                <FilterDropdownDownArrow />
              </div>
            </div>
            {isRoleDropdownOpen && (
              <div
                className="role-dropdown-overlay"
                onClick={() => setRoleDropdownOpen(false)}
              >
                <div
                  className="role-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="role-dropdown-user-section">
                    <MotifAvatar
                      description={user.name}
                      size="medium"
                      userName={user.name}
                      src={avatar}
                    />
                    <div className="role-dropdown-user-info">
                      <div className="role-dropdown-user-name">
                        {user?.name || HEADER.USER_NAME_FALLBACK}
                      </div>
                      <div className="role-dropdown-user-email">
                        {user?.username || ""}
                      </div>
                    </div>
                  </div>
                  <div className="role-dropdown-divider"></div>
                  <div className="role-dropdown-roles-section">
                    {distinctRoles.map((role, index) => {
                      const roleName =
                        typeof role === "object"
                          ? role.roleName || role.name
                          : role;
                      const isSelected =
                        typeof selectedRole === "object"
                          ? selectedRole?.userGuid === role?.userGuid
                          : selectedRole === role;
                      return (
                        <div
                          key={role?.userGuid || index}
                          className={`role-dropdown-item ${isSelected ? "selected" : ""}`}
                          onClick={() => handleRoleSelect(role)}
                        >
                          <span>{roleName}</span>
                          {isSelected && (
                            <div className="role-selected-tick">
                              <CheckIcon />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="role-dropdown-divider"></div>
                  <div className="role-dropdown-logout-section">
                    <div
                      className="role-dropdown-logout-item"
                      onClick={handleLogout}
                    >
                      <LogoutIcon width={16} height={16} />
                      <span>Log out</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        }
      />
      <NotificationsPanel
        open={isNotifOpen}
        onClose={() => setNotifOpen(false)}
        isLoading={notificationsLoading}
      />
    </>
  );
}

export default Header;
